import React, { useEffect, useRef, useState } from 'react'
import i18n from 'i18next'
import { Trans, useTranslation } from 'react-i18next'
import { useChineseLang } from '../../i18n/chinese'
import { getWordTypeClass, renderConverterWord } from 'utils/helperFunction'
import { PracticeWord } from 'types/writeSpeakTypes'

export interface WordTick {
  written: boolean
  heard: boolean
  /** Ticked by hand. Lives only in the browser and is never sent. */
  manual: boolean
}

export const isTicked = (t?: WordTick) => !!t && (t.written || t.heard || t.manual)

export const sourceLabel = (t?: WordTick): string => {
  if (!t) return ''
  const parts: string[] = []
  if (t.written) parts.push(i18n.t('writeSpeak:words.source.written'))
  if (t.heard) parts.push(i18n.t('writeSpeak:words.source.said'))
  return parts.join(' · ')
}

/** Runs a one-shot animation class the render after `value` turns true (never on mount). */
const useJustTurnedOn = (value: boolean): [boolean, () => void] => {
  const prev = useRef(value)
  const [on, setOn] = useState(false)
  useEffect(() => {
    if (value && !prev.current) setOn(true)
    prev.current = value
  }, [value])
  return [on, () => setOn(false)]
}

const StruggleIcon: React.FC = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true'>
    <path d='M12 3 2 20h20L12 3zm-1 6h2v5h-2zm0 7h2v2h-2z' />
  </svg>
)

// Mastery levels the API sends, as translation keys. Anything else shows as it came.
const MASTERY_KEYS: Record<string, string> = {
  New: 'writeSpeak:words.mastery.New',
  Learning: 'writeSpeak:words.mastery.Learning',
  Growing: 'writeSpeak:words.mastery.Growing',
  Developing: 'writeSpeak:words.mastery.Developing',
  Mastered: 'writeSpeak:words.mastery.Mastered',
}

const WordTag: React.FC<{ word: PracticeWord }> = ({ word }) => {
  const { t } = useTranslation('writeSpeak')
  if (word.struggling) {
    return (
      <span
        className='ws-tag struggling'
        title={word.lookups > 0 ? t('words.lookupsTitle', { count: word.lookups }) : undefined}
      >
        <StruggleIcon />
        {word.lookups > 0 ? t('words.strugglingLookups', { count: word.lookups }) : t('words.struggling')}
      </span>
    )
  }
  const boxes = getWordTypeClass(word.mastery) || []
  return (
    <span className='ws-tag'>
      <span className='process_box_main_div ws-meter' aria-hidden='true'>
        {boxes.map((cls, i) => (
          <span className={`process_box ${cls}`} key={i} />
        ))}
      </span>
      {MASTERY_KEYS[word.mastery as string] ? t(MASTERY_KEYS[word.mastery as string]) : word.mastery}
    </span>
  )
}

interface WordItemProps {
  word: PracticeWord
  tick?: WordTick
  onToggle: (word: string) => void
  onInfo: (word: PracticeWord, event: React.MouseEvent) => void
  infoActive: boolean
  disabled: boolean
  characterPreference?: string
}

const WordItem: React.FC<WordItemProps> = ({
  word,
  tick,
  onToggle,
  onInfo,
  infoActive,
  disabled,
  characterPreference,
}) => {
  const { t } = useTranslation('writeSpeak')
  const zhLang = useChineseLang()
  const ticked = isTicked(tick)
  const [pop, clearPop] = useJustTurnedOn(ticked)
  const src = sourceLabel(tick)
  const shown = renderConverterWord(word.word, characterPreference || '')
  return (
    <li className={`ws-word${ticked ? ' ticked' : ''}${pop ? ' pop' : ''}`} onAnimationEnd={clearPop}>
      <button
        type='button'
        className='ws-tickbtn'
        aria-pressed={ticked}
        aria-label={t(ticked ? 'words.tickLabelUsed' : 'words.tickLabelNotUsed', { word: shown, pinyin: word.pinyin })}
        onClick={() => onToggle(word.word)}
        disabled={disabled}
      >
        <span className='ws-check' aria-hidden='true'>
          <svg viewBox='0 0 12 12'>
            <path d='M2.5 6.2l2.3 2.3 4.7-4.9' />
          </svg>
        </span>
        <span className='ws-wtext'>
          <span className='ws-wline'>
            <span className='ws-wzh' lang={zhLang}>
              {shown}
            </span>
            <span className='ws-wpy'>{word.pinyin}</span>
            {src && <span className='ws-src'>{src}</span>}
          </span>
          <span className='ws-wline'>
            <WordTag word={word} />
          </span>
        </span>
      </button>
      <button
        type='button'
        className={`ws-info${infoActive ? ' word-active' : ''}`}
        data-ws-info=''
        aria-label={t('words.detailsLabel', { word: shown })}
        aria-expanded={infoActive}
        onClick={(e) => onInfo(word, e)}
      >
        <svg viewBox='0 0 24 24' aria-hidden='true'>
          <circle cx='12' cy='12' r='9' />
          <path d='M12 11v5M12 8h.01' />
        </svg>
      </button>
    </li>
  )
}

interface WordsListProps {
  words: PracticeWord[]
  ticks: Record<string, WordTick>
  onToggle: (word: string) => void
  onInfo: (word: PracticeWord, event: React.MouseEvent) => void
  activeInfoWord: string | null
  disabled?: boolean
  characterPreference?: string
}

export const WordsList: React.FC<WordsListProps> = ({
  words,
  ticks,
  onToggle,
  onInfo,
  activeInfoWord,
  disabled = false,
  characterPreference,
}) => (
  <ul className='ws-words'>
    {words.map((w) => (
      <WordItem
        key={w.word}
        word={w}
        tick={ticks[w.word]}
        onToggle={onToggle}
        onInfo={onInfo}
        infoActive={activeInfoWord === w.word}
        disabled={disabled}
        characterPreference={characterPreference}
      />
    ))}
  </ul>
)

/**
 * Phone-only row of pills under the answer box, shown while the keyboard is open. The card above scrolls out of
 * view as the learner types, so this is where they watch each word tick. Visual only: the card is the control.
 */
export const TypingStrip: React.FC<{
  words: PracticeWord[]
  ticks: Record<string, WordTick>
  characterPreference?: string
}> = ({ words, ticks, characterPreference }) => {
  const zhLang = useChineseLang()
  return (
    <div className='ws-type-strip' aria-hidden='true'>
      {words.map((w) => (
        <span key={w.word} className={`ws-type-pill${isTicked(ticks[w.word]) ? ' ticked' : ''}`} lang={zhLang}>
          <svg viewBox='0 0 12 12'>
            <path d='M2.5 6.2l2.3 2.3 4.7-4.9' />
          </svg>
          {renderConverterWord(w.word, characterPreference || '')}
        </span>
      ))}
    </div>
  )
}

/** "Words to use": the rail card on desktop and, on phones, the card inside the writing card. */
const WordsToTry: React.FC<WordsListProps> = (props) => {
  const { t } = useTranslation('writeSpeak')
  const { words, ticks } = props
  const total = words.length
  const used = words.filter((w) => isTicked(ticks[w.word])).length
  const allUsed = total > 0 && used === total

  // The counter rolls whenever it changes after the first paint.
  const prevUsed = useRef(used)
  const [rollKey, setRollKey] = useState(0)
  useEffect(() => {
    if (used !== prevUsed.current) setRollKey((k) => k + 1)
    prevUsed.current = used
  }, [used])

  // One soft shine across the card the moment the last word is used.
  const [shine, clearShine] = useJustTurnedOn(allUsed)

  if (!total) return null
  return (
    <div
      className={`ws-rail-card${shine ? ' shine' : ''}`}
      onAnimationEnd={(e) => {
        if (e.target === e.currentTarget) clearShine()
      }}
    >
      <div className='ws-rail-top'>
        <h5>{t('words.title')}</h5>
        <span className='ws-count' aria-live='polite'>
          {/* Keyed so the count's roll animation restarts on every change (Trans re-keys its children). */}
          <Trans
            key={rollKey}
            t={t}
            i18nKey='words.usedCount'
            values={{ used, total }}
            components={{ count: <b className={rollKey ? 'roll' : ''} /> }}
          />
        </span>
      </div>
      <div className='ws-dots' aria-hidden='true'>
        {words.map((w, i) => (
          <i key={w.word} className={i < used ? 'on' : ''} />
        ))}
      </div>
      <p className='ws-rail-hint'>{t('words.hint')}</p>
      <WordsList {...props} />
    </div>
  )
}

export default WordsToTry
